import { List } from 'immutable';
import { arrayMove } from '@dnd-kit/sortable';

export { isChangeProps } from '@/lib/propHelpers';

export const customArrayFlat = (arr) => arr.reduce((acc, val) => acc.concat(val), []);

export function executeFunc(func, ...args) {
  if (typeof func === 'function') {
    func(...args);
  }
}

export function listLoop(index, ar) {
  const count = ar.length;
  return {
    prev: ar[((index || count) - 1) % count],
    next: ar[(index + 1) % count],
  };
}

export function filterTags(tags) {
  return tags.filter((tag) => !tag.tag.startsWith('_'));
}

export const getSystemUser = (tag, systemUsers) =>
  tag.indexOf('@') >= 0 ? systemUsers.find((u) => u.userId === tag) : null;

export const sortTags = (tags, systemUsers) => {
  const res = tags.reduce(
    ([users, other], tag) => {
      const isUser = getSystemUser(tag.tag, systemUsers);
      return isUser ? [[...users, tag], other] : [users, [...other, tag]];
    },
    [[], []],
  );
  return customArrayFlat(res);
};

export function promisify(fn, arg) {
  return new Promise((resolve, reject) => {
    fn({ ...arg, resolve, reject });
  });
}

// eslint-disable-next-line max-len
export function promisifyCallback(fn, arg) {
  return new Promise((resolve) => {
    fn(arg, () => {
      resolve();
    });
  });
}

export function formatTagLabel(value) {
  if (typeof value === 'string' && value[0] === '#') {
    return value.slice(1);
  }

  return value;
}

export function findIndex(target) {
  return (a, v, i) => {
    if (v === target) return i;
    if (typeof a !== 'undefined') return a;
    return a;
  };
}

export const testPerformance = (name, fn, iteration = 1) => {
  const start = Date.now();
  for (let i = 0; i < iteration; i++) {
    fn();
  }
  const end = Date.now();
  // eslint-disable-next-line  no-console
  console.log(`${name}:`, (end - start) / 1000);
};

export const generateNumRange = (from, to) =>
  to >= from ? [...Array.from(Array(to - from).keys()).map((n) => n + from), to] : [];

export function debounceCancelable(delay, fn) {
  if (typeof fn !== 'function') {
    console.error(`debounceCancelable: callback is "${typeof fn}"`);
  }

  let timer = null;

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
    }
  };

  const debounceWrapper = (...args) => {
    cancel();
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, delay);
  };

  debounceWrapper.cancel = cancel;
  return debounceWrapper;
}

export function throttleCancelable(delay, fn) {
  let timer = null;

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
    }
  };

  const throttleWrapper = (...args) => {
    if (!timer) {
      fn(...args);
      timer = setTimeout(() => {
        timer = null;
      }, delay);
    }
  };

  throttleWrapper.cancel = cancel;
  return throttleWrapper;
}

export const extractEmail = (str) => {
  if (typeof str !== 'string') return null;
  const emails = str.match(/([a-zA-Z0-9.+_-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi);
  return emails && emails.length ? emails[0] : null;
};

export const isPromise = (p) => typeof p === 'object' && typeof p.then === 'function';

export const relevantSearch = (term, data, getSearchText) => {
  const lTerm = term.toLowerCase();
  const result = data.reduce((res, item) => {
    const value = getSearchText(item);
    const index = value.toLowerCase().indexOf(lTerm);
    const indexItems = index >= 0 ? res[index] || [] : null;

    if (indexItems) {
      res[index] = [...indexItems, item];
    }
    return res;
  }, []);

  return customArrayFlat(result);
};

export const rearrangeList = ({ oldIndex, newIndex, list }) =>
  List.isList ? new List(arrayMove(list.toArray(), oldIndex, newIndex)) : arrayMove(list, oldIndex, newIndex);

const mouseClickEvents = ['mousedown', 'click', 'mouseup'];
export const simulateMouseClick = (element) => {
  mouseClickEvents.forEach((mouseEventType) =>
    element.dispatchEvent(
      new MouseEvent(mouseEventType, {
        view: window,
        bubbles: true,
        cancelable: true,
      }),
    ),
  );
};

export const listToSelectOptions = (list) => list.map((item) => ({ value: item, label: item }));
